import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/tables/DataTable';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { formatDateTime } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';

const Overview = ({ onStartWorkNavigate,  navigateToMyTickets }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    const result = await engineerService.getDashboardData(user.id);
    if (result.success) {
      setDashboardData(result.data);
    }
    setLoading(false);
  };

  const handleStartWork = async (ticket) => {
    const result = await engineerService.startWork(ticket.id);
    if (result.success) {
      success('Work started on ticket!');
      if (typeof onStartWorkNavigate === 'function') {
        onStartWorkNavigate(result.data || ticket);
      } else {
        loadDashboard();
      }
    } else {
      error(result.error || 'Failed to start work');
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  // Styles
  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const statCardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px',
    transition: 'all 0.2s ease',
    cursor: 'default'
  };

  const statIconStyle = {
    width: '40px',
    height: '40px',
    marginBottom: '16px',
    opacity: '0.8'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
    marginBottom: '32px'
  };

  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px'
  };

  const chartContainerStyle = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '200px',
    gap: '12px',
    padding: '20px 0'
  };

  const barColumnStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    position: 'relative'
  };

  const barStyle = (heightPercent, color, id) => ({
    width: '100%',
    height: `${Math.max(heightPercent, 5)}%`,
    background: hoveredBar === id ? color : `${color}80`,
    borderRadius: '8px 8px 0 0',
    transition: 'all 0.3s ease',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '8px',
    cursor: 'pointer'
  });

  const barValueStyle = {
    fontSize: '14px',
    fontWeight: '700',
    color: 'white'
  };

  const tooltipStyle = {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'var(--text-primary)',
    color: 'var(--surface)',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    zIndex: 10,
    marginBottom: '8px'
  };

  const tooltipArrowStyle = {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '6px solid var(--text-primary)'
  };

  const sectionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  };

  const sectionTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  };

  // Table columns
  const columns = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type}
          </div>
        </div>
      )
    },
    {
      key: 'site_name',
      label: 'Site',
      sortable: true
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => formatDateTime(value)
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading dashboard...
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        No data available
      </div>
    );
  }

  const { stats, weeklyPerformance, priorityBreakdown, currentTickets } = dashboardData;
  const maxWeeklyCount = Math.max(...weeklyPerformance.map(d => d.count), 1);
  const maxPriorityCount = Math.max(...priorityBreakdown.map(d => d.count), 1);

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Stats Grid */}
      <div style={statsGridStyle}>
        <div
          style={{ ...statCardStyle, cursor: 'pointer' }}
          onClick={() => navigateToMyTickets('ASSIGNED_TODAY')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {/* <svg style={statIconStyle} viewBox="0 0 24 24" fill="var(--primary)">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg> */}
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
            ASSIGNED TODAY
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700',  marginBottom: '8px' }}>
            {stats.assignedToday}
          </div>
          <div style={{ fontSize: '12px',  }}>
            New tickets
          </div>
        </div>

        <div
          style={{ ...statCardStyle, cursor: 'pointer' }}
          onClick={() => navigateToMyTickets('IN_PROGRESS')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {/* <svg style={statIconStyle} viewBox="0 0 24 24" fill="#f59e0b">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14" stroke="white" fill="none" strokeWidth="2"/>
          </svg> */}
          <div style={{ fontSize: '12px',  fontWeight: '600', marginBottom: '8px' }}>
            IN PROGRESS
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
            {stats.inProgress}
          </div>
          <div style={{ fontSize: '12px', }}>
            Active tickets
          </div>
        </div>

        <div
          style={{ ...statCardStyle, cursor: 'pointer' }}
          onClick={() => navigateToMyTickets('COMPLETED_TODAY')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {/* <svg style={statIconStyle} viewBox="0 0 24 24" fill="#10b981">
            <polyline points="20 6 9 17 4 12" strokeWidth="2" stroke="#10b981" fill="none"/>
          </svg> */}
          <div style={{ fontSize: '12px',  fontWeight: '600', marginBottom: '8px' }}>
            COMPLETED TODAY
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700',  marginBottom: '8px' }}>
            {stats.completedToday}
          </div>
          <div style={{ fontSize: '12px',  fontWeight: '600' }}>
            Great job!
          </div>
        </div>

        <div
          style={statCardStyle}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {/* <svg style={statIconStyle} viewBox="0 0 24 24" fill="#10b981">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="6"/>
            <circle cx="12" cy="12" r="2"/>
          </svg> */}
          <div style={{ fontSize: '12px',  fontWeight: '600', marginBottom: '8px' }}>
            MY PERFORMANCE
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
            {stats.myPerformance}%
          </div>
          <div style={{ fontSize: '12px', fontWeight: '600' }}>
            Excellent!
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div style={gridStyle}>
        {/* Weekly Performance Chart */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
            My Weekly Performance
          </h3>
          <div style={chartContainerStyle}>
            {weeklyPerformance.map((day, index) => {
              const heightPercent = (day.count / maxWeeklyCount) * 100;
              const isToday = day.day === 'Today';
              
              return (
                <div 
                  key={day.day}
                  style={barColumnStyle}
                  onMouseEnter={() => setHoveredBar(`week-${index}`)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  <div style={barStyle(heightPercent, isToday ? 'var(--primary)' : '#10b981', `week-${index}`)}>
                    <div style={barValueStyle}>{day.count}</div>
                    {hoveredBar === `week-${index}` && (
                      <div style={tooltipStyle}>
                        {day.day}: {day.count} tickets completed
                        <div style={tooltipArrowStyle}></div>
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: isToday ? 'var(--primary)' : 'var(--text-primary)', 
                    textAlign: 'center'
                  }}>
                    {day.day}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Priority Breakdown Chart */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
            My Ticket Priorities
          </h3>
          <div style={chartContainerStyle}>
            {priorityBreakdown.map((item, index) => {
              const heightPercent = (item.count / maxPriorityCount) * 100;
              
              return (
                <div 
                  key={item.priority}
                  style={barColumnStyle}
                  onMouseEnter={() => setHoveredBar(`priority-${index}`)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  <div style={barStyle(heightPercent, item.color, `priority-${index}`)}>
                    <div style={barValueStyle}>{item.count}</div>
                    {hoveredBar === `priority-${index}` && (
                      <div style={tooltipStyle}>
                        {item.priority}: {item.count} tickets
                        <div style={tooltipArrowStyle}></div>
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: 'var(--text-primary)', 
                    textAlign: 'center'
                  }}>
                    {item.priority}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Tickets Table */}
      <div>
        <div style={sectionHeaderStyle}>
          <h3 style={sectionTitleStyle}>My Current Tickets</h3>
        </div>
        
        {currentTickets.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px', 
            color: 'var(--text-secondary)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px'
          }}>
            No active tickets assigned
          </div>
        ) : (
          <DataTable
            data={currentTickets}
            columns={columns}
            onRowClick={handleTicketClick}
            actions={[
              { 
                label: 'View', 
                onClick: handleTicketClick 
              },
              { 
                label: 'Start Work', 
                onClick: handleStartWork,
                condition: (ticket) => ticket.status !== TICKET_STATUSES.WORK_IN_PROGRESS
              }
            ]}
          />
        )}
      </div>

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTicket(null);
          }}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default Overview;